import ActionButton from 'components/ActionButton';
import React, { useEffect, useMemo, useState } from 'react';
import { showError, showSuccess } from 'utils';
import { AUDIENCE, getPageLimit, updatePageLimit } from 'utils/constant';
import styles from './index.module.scss';
import DataTable from 'components/DataTable';
import { tableStyles } from 'css/tableStyles';
import CustomLink from 'components/TableLink';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers';
import { Error } from 'components/message/Error';
import { getNamedAudience, sendPushNotificationByAudience } from 'services';
import Currency from 'components/Currency/Currency';

const validationSchema = yup.object().shape({
  title: yup.string().required('Title is required'),
  body: yup.string().required('Body is required'),
});

type FormData = {
  title: string;
  body: string;
};

const filters: { [p: string]: any; page: number } = {
  page: 0,
};

const SendPushNotification = (props) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState<Record<string, any>>({});
  const [days, setDays] = useState<number>(1);
  const [revenue, setRevenue] = useState<number>(0);
  const [omitTotalRevenuecolumn, setOmitTotalRevenueColumn] = useState<boolean>(false);
  const [selectedAudience, setSelectedAudience] = useState<string>(AUDIENCE.HIGH_REVENUE_USERS);
  const { register, handleSubmit, errors, setValue } = useForm<FormData>({
    resolver: yupResolver(validationSchema),
  });

  useEffect(() => {
    handleGetNamedAudience();
  }, []);

  const onChangePage = (page: number) => {
    filters.page = page - 1;
    handleGetNamedAudience();
  };

  const onChangeRowsPerPage = (rowsPerPage: number, currentPage: number) => {
    filters.limit = rowsPerPage;
    updatePageLimit(rowsPerPage);
    onChangePage(currentPage);
  };

  const getDataToSend = (dataToSend) => {
    if (selectedAudience === AUDIENCE.HIGH_REVENUE_USERS) {
      dataToSend.revenue = revenue;
    } else {
      dataToSend.days = days;
    }
  };

  const validateDataToSend = () => {
    if (selectedAudience !== AUDIENCE.HIGH_REVENUE_USERS) {
      if (days <= 0) {
        throw 'days must be greater than 0';
      }
      if (!days) {
        throw 'days is required';
      }
    } else {
      if (revenue < 0) {
        throw 'revenue must be greater than or equal to 0';
      }
      if (Number.isNaN(revenue)) {
        throw 'revenue is required';
      }
    }
  };

  const handleSendPushNotification = handleSubmit(
    async ({ title, body }) => {
      setLoading(true);
      const dataToSend: any = {
        audience: selectedAudience,
        title,
        body,
      };
      try {
        getDataToSend(dataToSend);
        validateDataToSend();
        const response = await sendPushNotificationByAudience(dataToSend);
        showSuccess(response);
        setValue('title', '');
        setValue('body', '');
      } catch (e) {
        showError(e);
      } finally {
        setLoading(false);
      }
    },
  );


  const handleGetNamedAudience = async () => {
    setLoading(true);
    const dataToSend: any = {
      audience: selectedAudience,
      page: filters.page,
      limit: getPageLimit(),
    };
    try {
      getDataToSend(dataToSend);
      validateDataToSend();
      const response = await getNamedAudience(dataToSend);
      setData(response);
    } catch (e) {
      showError(e);
    } finally {
      setLoading(false);
    }
  };

  const getActionButtons = () => {
    return (
      <div className={styles['footer']}>
        <ActionButton
          text={'Show Users'}
          active={!loading}
          isLoading={loading}
          onClick={handleGetNamedAudience}
        />
        <ActionButton
          text={'Send'}
          active={!loading}
          isLoading={loading}
          onClick={handleSendPushNotification}
        />
      </div>
    );
  };

  const columns = useMemo(() => [
    {
      name: 'UserId',
      selector: row => row.objectId,
      sortable: true,
      cell: row => (
        <CustomLink
          text={row.objectId}
          linkTo={`/users/${row.objectId}`}
        />
      ),
    },
    {
      name: 'Name',
      selector: row => row.full_name,
    },
    {
      name: 'Email',
      selector: row => row.email,
    }, {
      name: 'Total Revenue',
      selector: row => <Currency amount={row?.totalSubtotals} />,
      sortable: true,
      omit: omitTotalRevenuecolumn,
    },
  ], [omitTotalRevenuecolumn]);


  const getDaysInput = () => {
    const showDaysInput = () => {
      return (selectedAudience === AUDIENCE.ACTIVE_USERS || selectedAudience === AUDIENCE.INACTIVE_USERS || selectedAudience === AUDIENCE.NEW_USERS);
    };
    if (showDaysInput()) {
      return (
        <div className={styles['title']}>
          <div className={styles['label']}>Last X Days</div>
          <input
            name={'days'}
            className={'form-control'}
            type={'number'}
            ref={register}
            value={days}
            step={1}
            disabled={loading}
            onChange={e => setDays(parseInt(e.target.value))}
          />
        </div>
      );
    }
    else return null;
  };

  const getRevenueInput = () => {
    if (selectedAudience === AUDIENCE.HIGH_REVENUE_USERS) {
      return (
        <div className={styles['title']}>
          <div className={styles['label']}>More than Revenue</div>
          <input
            name={'revenue'}
            className={'form-control'}
            type={'number'}
            value={revenue}
            ref={register}
            step={1}
            disabled={loading}
            onChange={(e) => setRevenue(parseInt(e.target.value))}
          />
        </div>
      );
    }
    else return null;
  };

  const getAudienceOptions = () => {
    const handleSelect = (value: string) => {
      setSelectedAudience(value);
      setOmitTotalRevenueColumn(value !== AUDIENCE.HIGH_REVENUE_USERS);
      filters.page = 0;
    };
    const getUserOptions = () => {
      const audience = Object.values(AUDIENCE);
      return audience.map((i) => (
        <option key={i} value={i}>{i}</option>),
      );
    };
    return (
      <div className={styles['userId']}>
        <div className={styles['label']}>Select Audience</div>
        <select
          value={'Audience'}
          disabled={false}
          className={styles['audience']}
          onChange={(e) => handleSelect(e.target.value)}
        >
          <option value={selectedAudience} hidden={!!selectedAudience}>{selectedAudience}</option>
          {getUserOptions()}
        </select>
      </div>
    );
  };

  const getAudienceFilter = () => {
    return (
      <div className={'d-flex justify-content-between flex-column flex-lg-row flex-md-row'}>
        {getAudienceOptions()}
        {getDaysInput()}
        {getRevenueInput()}
      </div>
    );
  };

  const getFormInputs = () => {
    return (
      <div>
        <div className={styles['body']}>
          <div className={styles['label']}>Title</div>
          <input
            name={'title'}
            ref={register}
            className={'form-control'}
            disabled={loading}
          />
          {errors.title && <Error message={errors.title.message} />}
        </div>

        <div className={styles['body']}>
          <div className={styles['label']}>Body</div>
          <input
            name={'body'}
            ref={register}
            className={'form-control'}
            disabled={loading}
          />
          {errors.body && <Error message={errors.body.message} />}
        </div>
      </div>
    );
  };

  const getDataTable = () => {
    return (
      <DataTable
        columns={columns}
        noHeader={true}
        data={data.items}
        highlightOnHover={true}
        defaultSortField="createdAt"
        customStyles={tableStyles}
        keyField='objectId'
        pagination={true}
        paginationServer={true}
        paginationPerPage={getPageLimit()}
        paginationDefaultPage={(filters.page || 0) + 1}
        paginationTotalRows={data.total}
        paginationRowsPerPageOptions={[50, 100, 200]}
        onChangePage={onChangePage}
        onChangeRowsPerPage={onChangeRowsPerPage}
      />
    );
  };

  const getHeader = () => {
    return (
      <div className={styles['header']}>
        <div className={styles['heading']}>Send Push Notification By Audience</div>
      </div>
    );
  };

  return (
    <div className={styles['main-container']}>
      {getHeader()}
      <div className={styles['user-details']}>
        {getAudienceFilter()}
        {getFormInputs()}
        {getActionButtons()}
        {getDataTable()}
      </div>
    </div>
  );
};

export default SendPushNotification;
